import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useBusiness } from "../data/BusinessContext";
import { useLanguage } from "../i18n/LanguageContext";
import { DAY_KEYS } from "../lib/hours";

/**
 * Search-engine plumbing without a library: a title and description per
 * route, a canonical link, noindex for the staff area, and a Restaurant
 * schema.org block fed from the live business document (hours, address,
 * phone), so Google's listing follows what the dashboard says.
 */

const ORIGIN = "https://www.xn--dhm-qlaba.de";
const OG_IMAGE = "https://res.cloudinary.com/cric7vkq/image/upload/f_jpg,q_auto,c_fill,w_1200,h_630/v20260911/dahaeaem/site/interior";
const DAY_SCHEMA: Record<(typeof DAY_KEYS)[number], string> = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday",
};

function setMeta(selector: string, attr: "content" | "href", value: string, create: () => HTMLElement) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const meta = (name: string, value: string, key: "name" | "property" = "name") =>
  setMeta(`meta[${key}="${name}"]`, "content", value, () => {
    const m = document.createElement("meta");
    m.setAttribute(key, name);
    return m;
  });

export default function Seo() {
  const { pathname } = useLocation();
  const { t, language } = useLanguage();
  const business = useBusiness();

  useEffect(() => {
    const brand = `${t("brand")} · ${t("brandSub")} · ${t("brandLine")}`;
    const pages: Record<string, { title: string; description: string }> = {
      "/": { title: brand, description: `${t("heroEyebrow")} Heidelberg. ${t("heroLead")}` },
      "/menu": { title: `${t("navMenu")} · ${t("brand")} Heidelberg`, description: `${t("menuTitle")} ${t("menuLead")}` },
      "/reserve": { title: `${t("navReserve")} · ${t("brand")} Heidelberg`, description: `${t("reserveTitle")} ${t("reserveLead")}` },
      "/impressum": { title: `Impressum · ${t("brand")}`, description: `Impressum ${t("brand")}, Hauptstraße 129, 69117 Heidelberg.` },
      "/datenschutz": { title: `Datenschutzerklärung · ${t("brand")}`, description: `Datenschutzerklärung der Website ${t("brand")}.` },
    };
    const page = pages[pathname] ?? pages["/"];
    const staff = pathname.startsWith("/dashboard");
    const url = `${ORIGIN}${pathname === "/" ? "/" : pathname}`;

    document.title = staff ? `Dashboard · ${t("brand")}` : page.title;
    meta("description", page.description);
    meta("robots", staff ? "noindex, nofollow" : "index, follow");
    setMeta('link[rel="canonical"]', "href", url, () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    });
    meta("og:type", pathname === "/" ? "website" : "article", "property");
    meta("og:site_name", t("brand"), "property");
    meta("og:title", page.title, "property");
    meta("og:description", page.description, "property");
    meta("og:url", url, "property");
    meta("og:image", OG_IMAGE, "property");
    meta("og:locale", { de: "de_DE", en: "en_GB", fr: "fr_FR", es: "es_ES" }[language], "property");
    meta("twitter:card", "summary_large_image");
    meta("twitter:title", page.title);
    meta("twitter:description", page.description);
    meta("twitter:image", OG_IMAGE);
  }, [pathname, language, t]);

  // Structured data for the business, from the live document.
  useEffect(() => {
    const id = "ld-business";
    document.getElementById(id)?.remove();
    if (pathname.startsWith("/dashboard")) return;
    const hours = DAY_KEYS.filter((d) => business.hours[d]).map((d) => {
      const h = business.hours[d]!;
      // schema.org wants same-day ranges; a close past midnight is capped.
      const closes = h.close < h.open ? "23:59" : h.close;
      return { "@type": "OpeningHoursSpecification", dayOfWeek: DAY_SCHEMA[d], opens: h.open, closes };
    });
    const data = {
      "@context": "https://schema.org",
      "@type": "CafeOrCoffeeShop",
      name: business.name,
      alternateName: "Dähääm Heidelberg",
      description: "Kurpfälzer Wohnzimmer · Café — Bar in der Heidelberger Altstadt: Brunch, Kaffee und Kuchen, Cocktails und kleine Teller.",
      url: `${ORIGIN}/`,
      image: OG_IMAGE,
      ...(business.phone ? { telephone: business.phone } : {}),
      ...(business.email ? { email: business.email } : {}),
      address: {
        "@type": "PostalAddress",
        streetAddress: business.address,
        postalCode: business.city.split(" ")[0],
        addressLocality: business.city.split(" ").slice(1).join(" ") || "Heidelberg",
        addressCountry: "DE",
      },
      ...(business.mapsUrl ? { hasMap: business.mapsUrl } : {}),
      ...(business.instagram ? { sameAs: [business.instagram] } : {}),
      servesCuisine: ["Café", "Brunch", "Cocktails", "Mediterranean"],
      priceRange: "€€",
      openingHoursSpecification: hours,
      hasMenu: `${ORIGIN}/menu`,
      acceptsReservations: `${ORIGIN}/reserve`,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }, [business, pathname]);

  return null;
}
